import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";

import { cn } from "./utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium transition-all cursor-pointer disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "bg-walnut-600 hover:bg-walnut-700 text-white font-semibold",
        gradient:
          "bg-gradient-to-r from-walnut-600 to-walnut-700 dark:from-walnut-500 dark:to-walnut-600 text-white font-semibold hover:shadow-lg hover:shadow-walnut-500/30 transition-all duration-300 hover:scale-[1.02]",
        destructive:
          "bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border bg-background text-foreground hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        secondary:
          "bg-muted text-foreground hover:bg-muted/80 font-semibold",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline",
        glass:
          "backdrop-blur-[5px] bg-white/10 dark:bg-white/5 border border-white/20 dark:border-white/10 glass-shadow-outer hover:bg-white/20 dark:hover:bg-white/10 transition-all duration-300",
        success:
          "bg-order-status-delivered hover:bg-order-status-delivered/90 text-white font-semibold",
        info:
          "bg-order-status-received hover:bg-order-status-received/90 text-white font-semibold",
        warning:
          "bg-order-status-printing hover:bg-order-status-printing/90 text-white font-semibold",
      },
      size: {
        default: "h-10 px-4 py-2.5 text-sm rounded-xl has-[>svg]:px-3",
        sm: "h-8 px-3 py-2 text-sm rounded-lg gap-1.5 has-[>svg]:px-2.5",
        lg: "h-12 px-8 py-4 text-base rounded-xl has-[>svg]:px-6",
        icon: "size-9 rounded-lg",
        "icon-sm": "size-8 rounded-lg p-2",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Button({
  className,
  variant,
  size,
  pill = false,
  loading = false,
  asChild = false,
  disabled,
  children,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    pill?: boolean;
    loading?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      disabled={disabled || loading}
      className={cn(
        buttonVariants({ variant, size, className }),
        pill && "rounded-[33px]",
        loading && "cursor-not-allowed",
      )}
      {...props}
    >
      {loading ? (
        <>
          <Loader2 className="size-4 animate-spin" />
          {children}
        </>
      ) : (
        children
      )}
    </Comp>
  );
}

export { Button, buttonVariants };
